import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import TextField from '@material-ui/core/TextField';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { createStyles, IconButton, InputAdornment, makeStyles, Menu, MenuItem, Theme, useMediaQuery } from "@material-ui/core";
import LanguageIcon from '@material-ui/icons/Language';
import { UPDATE_LANGUAGE_PREFERENCE } from "../../graphql/user/languagePreference";
import { addUserDataAction } from "../../actions/auth/actions";
import { useDispatch, useSelector } from "react-redux";
import { IAppState } from "../../store";
import { useMutation } from "@apollo/client";
import US from "../../assets/images/US.png";
import UK from "../../assets/images/UK.png";
import Spanish from "../../assets/images/Spain.png";
import German from "../../assets/images/german.png";
import Greek from "../../assets/images/greek.png";
import Hebrew from "../../assets/images/Hebrew.png";
import Japanese from "../../assets/images/japanese.png";
import Polish from "../../assets/images/polish.png";
import Turkish from "../../assets/images/turkish.png";
import French from "../../assets/images/french.png";
import Arabic from "../../assets/images/arabic.png";
import Danish from "../../assets/images/danish.png";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      width: 160,
      marginRight: 10,
      padding: '2px 8px',
      borderRadius: '19px',
      backgroundColor: 'white',
      border: '1px solid #ccc',
      boxShadow: '0 2px 6px rgba(0,0,0,0.1)',
      
    },
    inputRoot: {
      padding: 0,
      borderRadius: '18px',
      "& .MuiAutocomplete-input": {
        padding: '4px 0 4px 4px',
        fontWeight: 600
      },
      
    }
  })
);


const getLanguageCodes = (country: string) => {
    return [
    {
      label: "English",
      flag: country === "US" ? US : UK,
      value: "en"
    },
    { label: "Español", flag: Spanish, value: "es" },
    { label: "Deutsch", flag: German, value: "de" },
    { label: "Ελληνικά", flag: Greek, value: "el" },
    { label: "עברית", flag: Hebrew, value: "he" },
    { label: "日本語", flag: Japanese, value: "ja" },
    { label: "Polski", flag: Polish, value: "pl" },
    { label: "Türkçe", flag: Turkish, value: "tr" },
    { label: "Français", flag: French, value: "fr" },
    { label: "العربية", flag: Arabic, value: "arSA" },
    { label: "Dansk", flag: Danish, value: "da" }
  ];
}

export default function LanguageSelector() {
  const classes = useStyles();
  const { i18n } = useTranslation();
  const userState = useSelector((state: IAppState) => state.authReducer.user);
  const website = useSelector((state: IAppState) => state.consumerWebsiteReducer.consumerWebsite);
  const { country } = website.organisation.address;
  const dispatch = useDispatch();
  const LanguageCodes = getLanguageCodes(country);

  const [lang, setLang] = useState<string>(i18n.language);
  const [openLanguageSelector, setOpenLanguageSelector] = useState(false);

  // Mobile detection
  const isMobile = useMediaQuery('(max-width:600px)');
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const changeLanguage = (code: string) => {
    setLang(code);
    i18n.changeLanguage(code);
    localStorage.setItem('lang', code);
    if (userState) {
      updateLanguagePreference({
        variables: {
          userId: userState?.id,
          selectedLanguage: code
        }
      })
    }
  };

  const [updateLanguagePreference] = useMutation(
    UPDATE_LANGUAGE_PREFERENCE, {
    onCompleted: (data) => {
      if (data) {
        if (userState) {
          dispatch(addUserDataAction({
            ...userState,
            currentLanguage: data.updateUserLanguagePreference.currentLanguage
          }))
        }

      }
    }
  }
  )

  useEffect(() => 
  { 
    if(userState?.currentLanguage){ 
    setLang(userState.currentLanguage);
    i18n.changeLanguage(userState.currentLanguage); 
    localStorage.setItem('lang', userState.currentLanguage); 
    } 
    else { 
    const storedLang = localStorage.getItem('lang'); 
    if (storedLang) { setLang(storedLang); 
    i18n.changeLanguage(storedLang); } } 
  },[userState])
  const handleMenuOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const selected = LanguageCodes.find(l => l.value === lang) || LanguageCodes[0];
  if (isMobile) {
    // Mobile: Button with dropdown
    return (
      <>
        <IconButton
          onClick={handleMenuOpen}
          aria-label="select language"
        >
        <img
          src={selected.flag}
          alt={selected.label}
          width={22}
          height={16}
        />
        </IconButton>
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
        >
          {LanguageCodes.map(option => (
            <MenuItem
              key={option.value}
              selected={option.value === lang}
              onClick={() => {
                changeLanguage(option.value);
                handleMenuClose();
              }}
            >
            <img
              src={option.flag}
              alt={option.label}
              width={20}
              height={14}
              style={{ marginRight: 8 }}
            />
            {option.label}
            </MenuItem>
          ))}
        </Menu>
      </>
    );
  }

  // Desktop: Autocomplete
  return (
    <Autocomplete
      classes={{
        root: classes.root,
        inputRoot: classes.inputRoot
      }}
      options={LanguageCodes}
      value={selected}
      open={openLanguageSelector}
      onOpen={() => setOpenLanguageSelector(true)}
      onClose={() => setOpenLanguageSelector(false)}
      getOptionLabel={(option) => `${option.label}`}
      popupIcon={<LanguageIcon />}
      renderInput={(params) => (
        <TextField
          {...params}
          variant="standard"
          InputProps={{
            ...params.InputProps,
            disableUnderline: true,
            startAdornment: (
              <InputAdornment position="start">
                <img
                  src={(LanguageCodes.find(l => l.value === lang) || LanguageCodes[0]).flag}
                  alt="flag"
                  width={20}
                  height={14}
                />
              </InputAdornment>
            ),
          }}
        />
      )}
      renderOption={(option) => (
        <li>
          <img
            src={option.flag}
            alt={option.label}
            width={20}
            height={14}
            style={{ marginRight: 8 }}
          />
          {option.label}
        </li>
      )}
      onChange={(event, value) => {
        if (value) {
          changeLanguage(value.value);
        } else {
          changeLanguage("en");
        }
      }}
    />
  );
}